// Create the map of api variable name to app variable name.
import { get, isPlainObject, transform } from "lodash";

const deepInvert = obj =>
  transform(obj, (res, val, key) => {
    if (isPlainObject(val)) {
      res[key] = deepInvert(val);
    } else {
      res[val] = key;
    }
  });

// Every key in the object should be mapped to a string when the attribute in the ModelType is a string
// Except when the attribute is an object, in that case it should be a nested SerializeMap of the object
export type SerializeMap<DataModel> = {
  [Attribute in keyof DataModel]: DataModel[Attribute] extends Array<infer Item>
    ? string | SerializeMap<Item>
    : DataModel[Attribute] extends object
      ? string | SerializeMap<DataModel[Attribute]>
      : string;
};

export const buildSerializer =
  <DataModel>(
    serializeMap: SerializeMap<DataModel>,
    modelName: string,
    nestedObjectMap: any /* The shape of this object cannot be strongly typed */ = {},
    defaultValues: Partial<DataModel> = null,
  ) =>
  (apiRequest, requestDefaultValues = null) =>
    apiRequest === null || apiRequest === undefined
      ? apiRequest
      : Object.entries(apiRequest).reduce(
          (acc, [key, value]) => {
            if (typeof serializeMap[key] === "object") {
              // Use the untransformed key
              const tk: string = get(deepInvert(nestedObjectMap), key, key);
              const subSerializer = buildSerializer(
                serializeMap[key],
                modelName,
                nestedObjectMap,
              );
              if (Array.isArray(value)) {
                acc[tk] = value.map(v => subSerializer(v));
              } else {
                acc[tk] = subSerializer(value);
              }
            } else {
              // TODO: Refactor everywhere that is using salelots with the unwanted snake_case
              // variable names then uncomment this.
              // if (!(key in serializeMap)) {
              //   console.warn(`${key} not in in ${modelName} map`);
              // }

              acc[serializeMap[key] || key] = value;
            }
            return acc;
          },
          { ...defaultValues, ...requestDefaultValues },
        );

export const buildDeserializer = <DataModel>(
  serializeMap: SerializeMap<DataModel>,
  modelName: string,
  nestedObjectMap: any /* The shape of this object cannot be strongly typed */ = {},
  defaultValues: Partial<DataModel> = null,
) => {
  const deserializeMap = deepInvert(serializeMap);

  return (apiResponse, responseDefaultValues = null) =>
    apiResponse === null || apiResponse === undefined
      ? apiResponse
      : Object.entries(apiResponse || {}).reduce(
          (acc, [key, value]) => {
            const tk = get(nestedObjectMap, key, key);
            if (typeof deserializeMap[tk] === "object") {
              // Call buildDeSerializer with serializeMap again so it doesn't double invert.
              const subSerializer = buildDeserializer(
                serializeMap[tk],
                modelName,
                nestedObjectMap,
              );
              if (Array.isArray(value)) {
                acc[tk] = value.map(v => subSerializer(v));
              } else {
                acc[tk] = subSerializer(value);
              }
            } else {
              // TODO: Refactor everywhere that is using salelots with the unwanted snake_case
              // variable names then uncomment this.
              // if (!(key in serializeMap)) {
              //   console.warn(`${key} not in in ${modelName} map`);
              // }
              acc[deserializeMap[tk] || tk] = value;
            }
            return acc;
          },
          { ...defaultValues, ...responseDefaultValues },
        );
};
